import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { MapPin, Clock, Users, ArrowLeft, Lock, Plus, Minus, Tag, CheckCircle, Star, KeyRound, Sparkles, Navigation } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';
import AuthModal from '../components/AuthModal';
import Reviews from '../components/Reviews';
import { ContactButton } from '../components/ContactForm';
import TrustSignals from '../components/TrustSignals';
import BadgeDisplay from '../components/BadgeDisplay';
import PromoBanner from '../components/PromoBanner';
import PrivateAccessGate from '../components/PrivateAccessGate';
import ShareTourQRCard from '../components/ShareTourQRCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_DEVICE_PRICE = 5.00;

const TourDetailPage = () => {
  const { tourId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token, user, login } = useAuth();
  const [tour, setTour] = useState(null);
  const [trust, setTrust] = useState(null);
  const [loading, setLoading] = useState(true);
  const promoSectionRef = useRef(null);
  const autoAppliedRef = useRef(false);
  const [purchasing, setPurchasing] = useState(false);
  const [additionalDevices, setAdditionalDevices] = useState(0);
  const [showAuthModal, setShowAuthModal] = useState(false);
  
  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [promoValidated, setPromoValidated] = useState(null);
  const [activeRedemptions, setActiveRedemptions] = useState([]);
  const [selectedStridesRedemption, setSelectedStridesRedemption] = useState(null);
  const [validatingPromo, setValidatingPromo] = useState(false);
  const [showPromoRedeemForm, setShowPromoRedeemForm] = useState(false);
  const [promoUserInfo, setPromoUserInfo] = useState({
    first_name: '',
    last_name: '',
    email: ''
  });
  const [redeemingPromo, setRedeemingPromo] = useState(false);

  // Private Access: per-session verification flag (sessionStorage, not localStorage)
  const [accessVerified, setAccessVerified] = useState(false);
  const autoStartedRef = useRef(false);

  // User's current location — used to compute distance to the tour's
  // starting point and decide whether to surface the "Get directions" CTA.
  // Silent fallback if denied or unsupported (no toast, no error).
  const [userLocation, setUserLocation] = useState(null);
  useEffect(() => {
    if (!('geolocation' in navigator)) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => { /* permission denied or unavailable — hide the CTA quietly */ },
      { enableHighAccuracy: false, maximumAge: 5 * 60 * 1000, timeout: 8000 }
    );
  }, []);

  // Distance + units helpers — locale-aware, mirroring the homepage logic so
  // a US visitor sees miles and everyone else sees km. Hidden when out of
  // realistic same-day reach (>500 km / ~310 mi).
  const usesMiles = (() => {
    try {
      const all = [navigator.language || '', ...(navigator.languages || [])].map((l) => l.toLowerCase());
      return all.some((l) => /-(us|gb|lr|mm)\b/.test(l));
    } catch (_) { return false; }
  })();
  const distanceKmToStart = (() => {
    if (!userLocation || !tour) return null;
    const lat = tour.representative_lat;
    const lng = tour.representative_lng;
    if (typeof lat !== 'number' || typeof lng !== 'number') return null;
    const R = 6371;
    const toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(lat - userLocation.lat);
    const dLng = toRad(lng - userLocation.lng);
    const a = Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(userLocation.lat)) * Math.cos(toRad(lat)) *
      Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  })();
  const formatDistance = (km) => {
    if (km === null || km === undefined) return null;
    if (usesMiles) {
      const miles = km * 0.621371;
      if (miles < 0.1) return `${Math.round(miles * 5280)} ft`;
      if (miles < 10) return `${miles.toFixed(1)} mi`;
      return `${Math.round(miles).toLocaleString()} mi`;
    }
    if (km < 1) return `${Math.round(km * 1000)} m`;
    if (km < 10) return `${km.toFixed(1)} km`;
    return `${Math.round(km).toLocaleString()} km`;
  };
  const NAV_THRESHOLD_KM = 500;
  const showDirectionsCTA =
    distanceKmToStart !== null &&
    distanceKmToStart <= NAV_THRESHOLD_KM &&
    tour && typeof tour.representative_lat === 'number' && typeof tour.representative_lng === 'number';
  const directionsHref = tour
    ? `https://www.google.com/maps/dir/?api=1&destination=${tour.representative_lat},${tour.representative_lng}&travelmode=driving`
    : '#';

  useEffect(() => {
    if (!tourId) return;
    try {
      const k = `audiostride_access_${tourId}`;
      setAccessVerified(sessionStorage.getItem(k) === 'verified');
    } catch (_) { /* sessionStorage unavailable */ }
  }, [tourId]);

  const handleAccessGranted = () => {
    try { sessionStorage.setItem(`audiostride_access_${tourId}`, 'verified'); } catch (_) { /* noop */ }
    setAccessVerified(true);
  };

  // Package B: After password unlock, if the tour is FREE, automatically
  // claim it and jump to the player so the user lands on the map
  // immediately. Paid tours still need user action so Stripe stays intact.
  // Only triggers once per page-load to avoid double-claim races.
  useEffect(() => {
    if (!tour || !accessVerified || autoStartedRef.current) return;
    if (!tour.is_private || !tour.is_free) return;
    if (!token) return; // requires auth — handleClaimFree opens AuthModal instead
    autoStartedRef.current = true;
    handleClaimFree();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tour, accessVerified, token]);

  const handleClaimFree = async () => {
    if (!token) { setShowAuthModal(true); return; }
    setPurchasing(true);
    try {
      const { data } = await axios.post(`${API}/tours/${tourId}/claim-free`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Access granted — enjoy the tour!');
      // Player route is /tour-player/:purchaseId (NOT /tours/:tourId/player).
      // Use the purchase_id returned from claim-free so the player can load.
      const pid = data?.purchase_id;
      if (!pid) throw new Error('No purchase id returned');
      navigate(`/tour-player/${pid}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not grant access');
    } finally {
      setPurchasing(false);
    }
  };

  // Pre-fill promo form with logged-in user's info
  useEffect(() => {
    if (user && user.email) {
      const nameParts = (user.name || '').split(' ');
      setPromoUserInfo({
        first_name: nameParts[0] || '',
        last_name: nameParts.slice(1).join(' ') || '',
        email: user.email
      });
    }
  }, [user]);

  // Load active Strides redemptions so the user can apply one at checkout
  useEffect(() => {
    if (!token || !tour || tour.is_free) {
      setActiveRedemptions([]);
      setSelectedStridesRedemption(null);
      return;
    }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/strides/my-redemptions`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setActiveRedemptions(data?.active || []);
      } catch (_) { setActiveRedemptions([]); }
    })();
  }, [token, tour]);

  useEffect(() => {
    fetchTour();
  }, [tourId]);

  // Track tour_page_viewed for the funnel (anonymous-friendly: uses synthetic session id)
  useEffect(() => {
    if (!tourId) return;
    let sessionId = sessionStorage.getItem('audiostride_anon_session');
    if (!sessionId) {
      sessionId = `anon-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      sessionStorage.setItem('audiostride_anon_session', sessionId);
    }
    const deviceType = /Mobile|Android|iPhone|iPad/.test(navigator.userAgent) ? 'mobile' : 'desktop';
    axios.post(`${API}/analytics/event`, {
      event_type: 'tour_page_viewed',
      tour_id: tourId,
      session_id: sessionId,
      email: user?.email || `anon-${sessionId}@audiostride.local`,
      device_type: deviceType,
    }).catch(() => {});
    // Referral funnel: mark this page-view as a `tour_view` attributed to
    // the stored ref_code (if any). Server silently no-ops if no code.
    try {
      const cached = localStorage.getItem('audiostride_ref');
      let refCode = null;
      if (cached) {
        try {
          const parsed = JSON.parse(cached);
          if (parsed?.code && (!parsed.expires_at || parsed.expires_at > Date.now())) {
            refCode = parsed.code;
          }
        } catch (_) {
          refCode = cached;
        }
      }
      if (refCode) {
        axios.post(`${API}/referrals/track-event`, {
          event_type: 'tour_view',
          ref_code: refCode,
          tour_id: tourId,
          session_id: sessionId,
        }).catch(() => {});
      }
    } catch (_) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourId]);

  // Auto-apply ?promo=CODE from URL query (runs once when tour loads)
  useEffect(() => {
    const urlPromo = searchParams.get('promo');
    if (!loading && tour && urlPromo && !autoAppliedRef.current) {
      autoAppliedRef.current = true;
      setPromoCode(urlPromo.toUpperCase());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, tour]);

  // Once promoCode is set from URL, kick off validation
  useEffect(() => {
    const urlPromo = searchParams.get('promo');
    if (!loading && tour && urlPromo && promoCode === urlPromo.toUpperCase() && !promoValidated && !validatingPromo) {
      handleValidatePromo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promoCode, loading, tour]);

  const applyFeaturedPromo = () => {
    const banner = tour?.promo_banner;
    if (!banner?.code) return;
    setPromoCode(banner.code);
    // Scroll promo section into view and trigger validation on next tick
    setTimeout(() => {
      promoSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      handleValidatePromo();
    }, 50);
  };

  const fetchTour = async () => {
    try {
      const [tourRes, trustRes] = await Promise.all([
        axios.get(`${API}/tours/${tourId}`),
        axios.get(`${API}/tours/${tourId}/trust`).catch(() => ({ data: null })),
      ]);
      setTour(tourRes.data);
      setTrust(trustRes.data);
    } catch (error) {
      toast.error('Failed to load tour');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async () => {
    if (!token) {
      setShowAuthModal(true);
      return;
    }

    setPurchasing(true);
    try {
      const originUrl = window.location.origin;
      const checkoutData = {
        tour_id: tourId,
        additional_devices: additionalDevices,
        origin_url: originUrl,
        tour_version: 'full_access', // User gets access to both versions
      };
      
      // Include promo code if a percentage discount is applied (not free/100%)
      if (promoValidated && promoValidated.discount_percent && promoValidated.discount_percent < 100 && promoValidated.discount_percent > 0) {
        checkoutData.promo_code = promoCode;
      }
      // Include a redeemed Strides discount if the user picked one
      if (selectedStridesRedemption?.redemption_id) {
        checkoutData.strides_redemption_id = selectedStridesRedemption.redemption_id;
      }
      
      console.log('Sending checkout request...', checkoutData);
      
      const response = await axios.post(`${API}/purchases/checkout`, checkoutData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('Checkout response:', response.data);

      // Redirect to Stripe checkout
      if (response.data && response.data.url) {
        console.log('Redirecting to Stripe:', response.data.url);
        window.location.href = response.data.url;
      } else {
        toast.error('Invalid checkout response');
        setPurchasing(false);
      }
    } catch (error) {
      console.error('Checkout error:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to initiate purchase';
      toast.error(errorMsg);
      setPurchasing(false);
    }
  };

  const incrementDevices = () => {
    setAdditionalDevices(prev => prev + 3);
  };

  const decrementDevices = () => {
    setAdditionalDevices(prev => Math.max(0, prev - 3));
  };

  const handleValidatePromo = async () => {
    if (!promoCode.trim()) {
      toast.error('Please enter a promo code');
      return;
    }
    
    setValidatingPromo(true);
    try {
      const response = await axios.post(`${API}/promo-codes/validate`, {
        code: promoCode,
        tour_id: tourId
      });
      setPromoValidated(response.data);
      toast.success(response.data.message);
      
      // Only show redeem form for FREE tours (100% off)
      // For partial discounts, user still needs to pay via Stripe
      const isFreeCode = response.data.discount_type === 'free' || response.data.discount_percent === 100;
      setShowPromoRedeemForm(isFreeCode);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid promo code');
      setPromoValidated(null);
    } finally {
      setValidatingPromo(false);
    }
  };

  const handleRedeemPromo = async (e) => {
    e.preventDefault();
    
    if (!promoUserInfo.first_name || !promoUserInfo.last_name || !promoUserInfo.email) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setRedeemingPromo(true);
    try {
      const response = await axios.post(`${API}/promo-codes/redeem`, {
        code: promoCode,
        tour_id: tourId,
        first_name: promoUserInfo.first_name,
        last_name: promoUserInfo.last_name,
        email: promoUserInfo.email,
        additional_devices: additionalDevices,
        tour_version: 'full_access'  // Combined tour - user chooses version when playing
      });
      
      // Auto-login the user with the returned token
      if (response.data.token && response.data.user) {
        login(response.data.token, response.data.user);
        toast.success('Tour unlocked! Check "My Tours" to start exploring.');
        navigate('/my-tours');
      } else if (token) {
        // User was already logged in
        toast.success('Tour unlocked! Check "My Tours" to start exploring.');
        navigate('/my-tours');
      } else {
        // Fallback - shouldn't happen with new backend
        localStorage.setItem('promo_email', promoUserInfo.email);
        toast.success('Tour unlocked! Please log in with your email to access it.');
        navigate('/');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to redeem promo code');
    } finally {
      setRedeemingPromo(false);
    }
  };

  const resetPromo = () => {
    setPromoCode('');
    setPromoValidated(null);
    setShowPromoRedeemForm(false);
    setPromoUserInfo({ first_name: '', last_name: '', email: '' });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-cyan-500 border-t-transparent"></div>
      </div>
    );
  }

  if (!tour) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Lock className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 font-body">Tour not found</p>
        </div>
      </div>
    );
  }

  const devicePrice = tour?.group_addon_price || DEFAULT_DEVICE_PRICE;
  const deviceGroupCost = Math.ceil(additionalDevices / 3) * devicePrice;
  
  // Single price - no more express vs comprehensive pricing
  const basePrice = tour?.base_price || 0;
  const discountPercent = promoValidated?.discount_percent || 0;
  const isFreePromo = promoValidated && (promoValidated.discount_type === 'free' || discountPercent === 100);
  const isPercentDiscount = promoValidated && promoValidated.discount_type === 'percent' && discountPercent > 0 && discountPercent < 100;
  
  // For free promos, price is $0. For percent discounts, apply the percentage.
  let discountedBasePrice = basePrice;
  if (isFreePromo) {
    discountedBasePrice = 0;
  } else if (isPercentDiscount) {
    discountedBasePrice = basePrice * (1 - discountPercent / 100);
  }
  const totalPrice = discountedBasePrice + deviceGroupCost;

  // Private tours: gate everything behind the access screen until verified.
  // Admins DO see the gate too — they should test the real flow and know their own code.
  if (tour.is_private && !accessVerified) {
    return (
      <PrivateAccessGate
        tour={tour}
        onAccessGranted={handleAccessGranted}
        onBack={() => navigate('/')}
      />
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="bg-cyan-500 text-white py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => navigate('/')}
            data-testid="back-to-home-button"
            className="flex items-center text-white hover:text-cyan-100 mb-6 transition-colors font-bold uppercase tracking-wide"
          >
            <ArrowLeft className="w-6 h-6 mr-2" />
            Back to Tours
          </button>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Advertised Free/Discount Promo Banner */}
        {tour.promo_banner && (
          <PromoBanner banner={tour.promo_banner} onApply={applyFeaturedPromo} />
        )}

        <div className="bg-white border-4 border-cyan-500 rounded-2xl shadow-card overflow-hidden">
          <div className="relative h-72 bg-gradient-to-br from-cyan-400 to-blue-500">
            {tour.cover_image_url && (
              <img 
                src={tour.cover_image_url} 
                alt={tour.title}
                className="absolute inset-0 w-full h-full object-cover"
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/50 to-transparent"></div>
            <div className="absolute inset-0 flex items-end p-6 sm:p-8">
              <div className="w-full">
                <h1 className="text-4xl sm:text-5xl font-black text-white mb-2 uppercase leading-tight">{tour.title}</h1>
                <p className="text-cyan-50 text-xl sm:text-2xl font-bold uppercase mb-3">{tour.city}</p>
                {/* Badges directly beneath city line */}
                {tour.badges && tour.badges.length > 0 && (
                  <BadgeDisplay badges={tour.badges} size="sm" className="mb-1" />
                )}
              </div>
            </div>
          </div>

          {/* Trust Signals Strip — above the fold */}
          {trust && (
            <div className="px-6 sm:px-8 py-4 bg-gradient-to-r from-cyan-50 via-white to-amber-50 border-b-2 border-cyan-100">
              <TrustSignals trust={trust} />
            </div>
          )}

          <div className="p-8 bg-gray-50">
            <div className="flex flex-wrap gap-6 mb-8 pb-6 border-b-2 border-cyan-200">
              <div className="flex items-center text-gray-800">
                <Clock className="w-6 h-6 mr-3 text-cyan-500" />
                <span className="font-bold text-lg">{tour.duration}</span>
              </div>
              <div className="flex items-center text-gray-800">
                <MapPin className="w-6 h-6 mr-3 text-cyan-500" />
                <span className="font-bold text-lg">{tour.stop_count || 0} stops</span>
              </div>
              <div className="flex items-center text-gray-800">
                <MapPin className="w-6 h-6 mr-3 text-cyan-500" />
                <span className="font-bold text-lg">GPS-guided</span>
              </div>
            </div>

            {/* Navigate to start — only when the user has shared location AND
                the starting point is within realistic same-day reach. Opens
                native Apple Maps on iOS, Google Maps on Android, or a tab on
                desktop via the universal Google Maps dir API. */}
            {showDirectionsCTA && (
              <div className="mb-8 -mt-4">
                <a
                  href={directionsHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="tour-detail-directions-cta"
                  className="group flex items-center justify-between gap-4 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 hover:from-emerald-600 hover:via-teal-600 hover:to-cyan-600 text-white rounded-2xl px-5 py-4 shadow-md hover:shadow-xl active:scale-[0.99] transition-all"
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                      <Navigation className="w-6 h-6" />
                    </div>
                    <div className="min-w-0 text-left">
                      <p className="text-[11px] uppercase tracking-wide font-bold opacity-90">Get to the starting point</p>
                      <p className="font-black text-base sm:text-lg truncate">
                        Navigate to {tour.city} · {formatDistance(distanceKmToStart)} {usesMiles ? 'away' : 'away'}
                      </p>
                    </div>
                  </div>
                  <span className="text-xs font-bold uppercase tracking-wide opacity-90 group-hover:opacity-100 flex-shrink-0 hidden sm:inline">
                    Open in Maps →
                  </span>
                </a>
              </div>
            )}

            <div className="mb-10">
              <h3 className="text-2xl font-black uppercase text-gray-900 mb-4">About This Tour</h3>
              <p className="text-gray-700 text-lg leading-relaxed mb-6">{tour.description}</p>

              {/* Starting Point section — shows Full and (when applicable)
                  Express starting stops with dedicated Get Directions buttons.
                  Rendered before Preview so travelers know where to head. */}
              {tour.starting_stop_full && (() => {
                const fullStart = tour.starting_stop_full;
                const exStart = tour.starting_stop_express;
                const hasSeparateExpress = exStart && exStart.title && exStart.title !== fullStart.title;
                const dir = (lat, lng) =>
                  `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${lat},${lng}`)}&travelmode=walking`;
                return (
                  <div className="mb-6 rounded-2xl border-2 border-cyan-200 bg-gradient-to-br from-cyan-50 to-white overflow-hidden" data-testid="tour-detail-starting-points">
                    <div className="px-5 py-3 bg-cyan-500 text-white flex items-center gap-2">
                      <MapPin className="w-5 h-5" />
                      <h4 className="text-base font-black uppercase tracking-wider">
                        {hasSeparateExpress ? 'Where the tour starts' : 'Where the tour starts'}
                      </h4>
                    </div>
                    <div className="divide-y divide-cyan-100">
                      <div className="px-5 py-4 flex items-center gap-3">
                        <div className="shrink-0 w-10 h-10 rounded-full bg-cyan-100 text-cyan-700 flex items-center justify-center font-black">
                          📍
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[11px] uppercase tracking-widest font-bold text-cyan-700">
                            {hasSeparateExpress ? 'Full tour starts at' : 'Starts at'}
                          </p>
                          <p className="text-lg font-bold text-slate-900 truncate" data-testid="tour-detail-start-full-title">
                            {fullStart.title}
                          </p>
                        </div>
                        <a
                          href={dir(fullStart.lat, fullStart.lng)}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid="tour-detail-start-full-dir"
                          className="shrink-0 inline-flex items-center gap-1.5 text-sm font-bold text-white bg-cyan-600 hover:bg-cyan-700 rounded-full px-4 py-2 transition-colors active:scale-95"
                        >
                          <Navigation className="w-4 h-4" />
                          Directions
                        </a>
                      </div>
                      {hasSeparateExpress && (
                        <div className="px-5 py-4 flex items-center gap-3 bg-amber-50/60">
                          <div className="shrink-0 w-10 h-10 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center font-black text-lg">
                            ⚡
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[11px] uppercase tracking-widest font-bold text-amber-700">
                              Express starts at
                            </p>
                            <p className="text-lg font-bold text-slate-900 truncate" data-testid="tour-detail-start-express-title">
                              {exStart.title}
                            </p>
                          </div>
                          <a
                            href={dir(exStart.lat, exStart.lng)}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="tour-detail-start-express-dir"
                            className="shrink-0 inline-flex items-center gap-1.5 text-sm font-bold text-amber-900 bg-white hover:bg-amber-100 border-2 border-amber-300 rounded-full px-4 py-2 transition-colors active:scale-95"
                          >
                            <Navigation className="w-4 h-4" />
                            Directions
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Admin-only Test Mode entry — invisible to regular users */}
              {user?.account_type === 'admin' && (
                <div className="mb-6 bg-purple-50 border-2 border-purple-300 rounded-xl p-4 flex flex-wrap items-center gap-3" data-testid="admin-test-banner">
                  <div className="flex-1 min-w-[200px]">
                    <div className="font-black text-purple-900 uppercase tracking-wider text-sm">Admin: Test Mode</div>
                    <div className="text-xs text-purple-700">Walk through the tour from any saved test location (Bauhaus, etc.) — audio fires as if you're really there.</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate(`/tour-player/admin-test-${tour.tour_id}?mode=test`)}
                    data-testid="open-admin-test-mode"
                    className="px-5 py-2.5 rounded-full bg-purple-900 hover:bg-purple-950 text-white font-black text-sm uppercase tracking-wider transition active:scale-95"
                  >
                    Open Test Mode
                  </button>
                </div>
              )}
              
              {tour.preview_audio_url && (
                <div className="bg-blue-50 border-2 border-blue-400 rounded-xl p-6">
                  <div className="flex items-center mb-3">
                    <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mr-4">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-xl font-black uppercase text-gray-900">Audio Preview</h4>
                      <p className="text-sm text-gray-600 font-semibold">Listen to a sample before you buy</p>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-4">
                    {tour.preview_audio_url.includes('soundcloud.com') ? (
                      <iframe
                        width="100%"
                        height="166"
                        scrolling="no"
                        frameBorder="no"
                        allow="autoplay"
                        src={`https://w.soundcloud.com/player/?url=${encodeURIComponent(tour.preview_audio_url)}&color=%2300bcd4&auto_play=false&hide_related=true&show_comments=false&show_user=false&show_reposts=false&show_teaser=false`}
                        data-testid="audio-preview-player"
                      ></iframe>
                    ) : (
                      <audio 
                        controls 
                        className="w-full"
                        data-testid="audio-preview-player"
                      >
                        <source 
                          src={tour.preview_audio_url.startsWith('/') ? `${BACKEND_URL}${tour.preview_audio_url}` : tour.preview_audio_url} 
                          type="audio/mpeg" 
                        />
                        Your browser does not support the audio element.
                      </audio>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Share QR card — Apple-Wallet-pass styled; lets a visitor hand
                a friend their phone and one-scan handoff this exact tour. No
                referral/Strides hook (Phase 1+2 still on hold for prod). */}
            <div className="mb-10" data-testid="share-tour-qr-section">
              <ShareTourQRCard tour={tour} />
            </div>

            <div className="border-t-4 border-cyan-500 pt-8">
              <h3 className="text-3xl font-black uppercase text-gray-900 mb-6">
                {tour.is_free ? 'Start This Experience' : 'Get This Tour'}
              </h3>

              {tour.is_free && (
                /* Free tour: skip Stripe entirely, claim & go */
                <div className="space-y-4" data-testid="free-tour-cta">
                  <div className="p-6 bg-gradient-to-br from-emerald-50 to-blue-50 border-2 border-emerald-300 rounded-2xl">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-2xl">✨</span>
                      <span className="font-black text-2xl text-emerald-900 uppercase">Complimentary Access</span>
                    </div>
                    <p className="text-emerald-800 text-base">
                      This experience is on us. Tap below to begin.
                    </p>
                  </div>
                  <button
                    onClick={handleClaimFree}
                    disabled={purchasing}
                    data-testid="start-free-tour-btn"
                    className="w-full bg-slate-900 hover:bg-black disabled:bg-gray-300 text-white py-5 rounded-full font-black text-2xl uppercase shadow-xl hover:shadow-2xl transition-all active:scale-95 disabled:cursor-not-allowed tracking-wide"
                  >
                    {purchasing ? 'Granting access…' : 'Begin Experience'}
                  </button>
                  <p className="text-sm text-gray-600 text-center">
                    No payment required.
                  </p>
                </div>
              )}

              {!tour.is_free && (
              <>
              <div className="space-y-6">
                {/* Tour Info Card */}
                <div className="p-6 bg-white border-4 border-cyan-400 rounded-xl">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <span className="font-black text-xl text-gray-900 uppercase">
                        📍 {tour.title}
                      </span>
                      <p className="text-gray-500 text-sm mt-1">1 Device included • GPS-guided audio tour</p>
                    </div>
                    <span className="text-3xl font-black text-cyan-600">
                      ${tour.base_price.toFixed(2)}
                    </span>
                  </div>
                  
                  {/* Features list */}
                  <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-cyan-500" />
                      <span>{tour.duration}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-cyan-500" />
                      <span>{tour.stop_count || 0} stops</span>
                    </div>
                  </div>
                  
                  {/* Express option note - only show if tour has express enabled */}
                  {tour.express_enabled && (
                    <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="tour-detail-express-note">
                      <div className="flex items-start gap-2 text-amber-900">
                        <span className="text-lg leading-none pt-0.5">⚡</span>
                        <div className="text-sm">
                          <p className="font-bold">Both versions included in one price.</p>
                          <p className="text-amber-800">
                            Full tour + express highlights ({tour.express_stop_count || 0} stops) —
                            pick either version when you start. 7-day access to both.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Additional Devices Section - Only show if tour explicitly allows it */}
                {tour.allow_additional_devices === true && (
                <div className="p-6 border-4 border-blue-400 rounded-xl bg-blue-50">
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center">
                        <Users className="w-6 h-6 mr-3 text-blue-600" />
                        <span className="font-black text-xl text-gray-900 uppercase">Add More Devices</span>
                      </div>
                    </div>
                    <p className="text-base text-gray-700 font-semibold mb-4">
                      Share synchronized audio with friends walking together. Additional devices hear the main user&apos;s audio in real-time.
                    </p>
                    <p className="text-sm text-blue-700 font-bold mb-4">
                      ${devicePrice.toFixed(2)} per 3 additional devices
                    </p>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-white rounded-lg">
                    <div className="flex items-center gap-4">
                      <button
                        onClick={decrementDevices}
                        disabled={additionalDevices === 0}
                        data-testid="decrease-devices-button"
                        className="w-10 h-10 flex items-center justify-center bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 text-white rounded-full font-bold transition-all active:scale-95 disabled:cursor-not-allowed"
                      >
                        <Minus className="w-5 h-5" />
                      </button>
                      <div className="text-center min-w-[100px]">
                        <p className="text-3xl font-black text-blue-600">{additionalDevices}</p>
                        <p className="text-xs text-gray-600 font-semibold">extra devices</p>
                      </div>
                      <button
                        onClick={incrementDevices}
                        data-testid="increase-devices-button"
                        className="w-10 h-10 flex items-center justify-center bg-blue-500 hover:bg-blue-600 text-white rounded-full font-bold transition-all active:scale-95"
                      >
                        <Plus className="w-5 h-5" />
                      </button>
                    </div>
                    {additionalDevices > 0 && (
                      <div className="text-right">
                        <p className="text-sm text-gray-600 font-semibold">Device Cost</p>
                        <p className="text-2xl font-black text-blue-600">+${deviceGroupCost.toFixed(2)}</p>
                      </div>
                    )}
                  </div>
                </div>
                )}

                <div className="flex items-center justify-between p-6 bg-cyan-500 text-white rounded-xl border-4 border-cyan-600">
                  <span className="font-black text-2xl uppercase">Total</span>
                  <div className="text-right">
                    {(isPercentDiscount || isFreePromo) && (
                      <span className="text-lg line-through opacity-70 mr-2">${(basePrice + deviceGroupCost).toFixed(2)}</span>
                    )}
                    <span className="text-4xl font-black">
                      {isFreePromo ? 'FREE' : `$${totalPrice.toFixed(2)}`}
                    </span>
                  </div>
                </div>
              </div>

              {/* Promo Code Section - Always Visible */}
              <div ref={promoSectionRef} className="mt-6 p-3 sm:p-6 bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl">
                <div className="flex items-center mb-4">
                  <Tag className="w-5 h-5 sm:w-6 sm:h-6 mr-2 text-purple-600 flex-shrink-0" />
                  <h4 className="font-bold text-base sm:text-lg text-gray-900">Have a Promo Code?</h4>
                </div>
                
                {/* Phase 2: Apply a Strides discount if user has any active redemptions */}
                {token && activeRedemptions.length > 0 && (
                  <div className="mb-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-3" data-testid="strides-redemption-picker">
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-bold text-sm text-slate-900 flex items-center gap-1.5">
                        <Sparkles className="w-4 h-4 text-amber-500" />
                        Apply a Strides discount
                      </p>
                      {selectedStridesRedemption && (
                        <button
                          type="button"
                          onClick={() => setSelectedStridesRedemption(null)}
                          data-testid="clear-redemption"
                          className="text-xs text-rose-600 font-bold"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                    <div className="space-y-1.5">
                      {activeRedemptions.map((r) => {
                        const selected = selectedStridesRedemption?.redemption_id === r.redemption_id;
                        return (
                          <button
                            key={r.redemption_id}
                            type="button"
                            onClick={() => setSelectedStridesRedemption(selected ? null : r)}
                            data-testid={`apply-redemption-${r.redemption_id}`}
                            className={`w-full text-left p-2.5 rounded-lg border-2 transition active:scale-[0.99] ${selected ? 'border-amber-500 bg-amber-100' : 'border-amber-200 bg-white hover:border-amber-400'}`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-black text-slate-900 text-sm">${(r.discount_usd_cents / 100).toFixed(2)} off</p>
                                <p className="text-[10px] text-slate-500">Expires {new Date(r.expires_at).toLocaleDateString()}</p>
                              </div>
                              <span className={`text-xs font-bold ${selected ? 'text-amber-700' : 'text-slate-400'}`}>{selected ? '✓ Selected' : 'Use'}</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-[11px] text-slate-500 mt-2">
                      Don't have any? <a href="/profile" className="text-teal-600 font-bold underline">Redeem Strides</a> on your profile.
                    </p>
                  </div>
                )}
                
                {!showPromoRedeemForm ? (
                  <div className="space-y-3">
                    {/* Input field - optimized for mobile.
                        - inputMode="text" gives Android the standard keyboard.
                        - Inline fontSize 16px prevents mobile browser zoom-on-focus.
                        - Larger touch target (min 56px) for chunky fingers. */}
                    <input
                      type="text"
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && promoCode.trim()) {
                          e.preventDefault();
                          handleValidatePromo();
                        }
                      }}
                      data-testid="promo-code-input"
                      placeholder="e.g. WELCOME10"
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="characters"
                      spellCheck="false"
                      inputMode="text"
                      style={{ fontSize: '16px' }}
                      className="w-full min-h-[56px] px-4 py-3 sm:py-4 rounded-lg border-2 border-purple-300 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all font-mono uppercase text-center tracking-widest placeholder:tracking-normal placeholder:text-purple-300 placeholder:font-sans placeholder:normal-case"
                    />
                    {/* Apply button - full width, touch-friendly */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (promoCode.trim()) {
                          handleValidatePromo();
                        } else {
                          toast.error('Please enter a promo code');
                        }
                      }}
                      disabled={validatingPromo}
                      data-testid="apply-promo-button"
                      className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 text-white px-4 sm:px-8 py-3 sm:py-4 rounded-lg font-bold text-base sm:text-xl transition-all active:scale-95 disabled:cursor-not-allowed touch-manipulation"
                    >
                      {validatingPromo ? 'Checking...' : 'APPLY CODE'}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <CheckCircle className="w-6 h-6 text-green-500 mr-2" />
                        <span className="font-bold text-green-700 text-lg">
                          {promoValidated?.message}
                        </span>
                      </div>
                      <button
                        onClick={resetPromo}
                        className="text-sm text-gray-500 hover:text-gray-700"
                      >
                        Use different code
                      </button>
                    </div>
                    
                    {/* Only show redeem form for FREE tours (100% off) */}
                    {(promoValidated?.discount_type === 'free' || promoValidated?.discount_percent === 100) && (
                      <div className="bg-white p-4 rounded-lg border border-purple-200">
                        <p className="text-sm text-gray-600 mb-4 font-semibold">
                          Enter your details to redeem this free tour:
                        </p>
                        <form onSubmit={(e) => e.preventDefault()} className="space-y-3">
                          <div className="grid grid-cols-2 gap-3">
                            <input
                              type="text"
                              value={promoUserInfo.first_name}
                              onChange={(e) => setPromoUserInfo({ ...promoUserInfo, first_name: e.target.value })}
                              data-testid="promo-first-name"
                              placeholder="First Name"
                              autoComplete="given-name"
                              className="px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all"
                            />
                            <input
                              type="text"
                              value={promoUserInfo.last_name}
                              onChange={(e) => setPromoUserInfo({ ...promoUserInfo, last_name: e.target.value })}
                              data-testid="promo-last-name"
                              placeholder="Last Name"
                              autoComplete="family-name"
                              className="px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all"
                            />
                          </div>
                          <input
                            type="email"
                            value={promoUserInfo.email}
                            onChange={(e) => setPromoUserInfo({ ...promoUserInfo, email: e.target.value })}
                            data-testid="promo-email"
                            placeholder="Email Address"
                            required
                            autoComplete="email"
                            className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all"
                          />
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (promoUserInfo.first_name && promoUserInfo.last_name && promoUserInfo.email) {
                                handleRedeemPromo(e);
                              } else {
                                toast.error('Please fill in all fields');
                              }
                            }}
                            onTouchEnd={(e) => {
                              e.preventDefault();
                              if (promoUserInfo.first_name && promoUserInfo.last_name && promoUserInfo.email) {
                                handleRedeemPromo(e);
                              } else {
                                toast.error('Please fill in all fields');
                              }
                            }}
                            disabled={redeemingPromo}
                            data-testid="redeem-promo-button"
                            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:from-gray-300 disabled:to-gray-300 text-white py-4 rounded-full font-black text-xl uppercase shadow-xl hover:shadow-2xl transition-all active:scale-95 disabled:cursor-not-allowed touch-manipulation"
                          >
                            {redeemingPromo ? 'Redeeming...' : 'Redeem Free Tour'}
                          </button>
                        </form>
                      </div>
                    )}
                    
                    {/* For percentage discounts, show the discounted price info */}
                    {promoValidated && 
                     (promoValidated.discount_type === 'percent' || promoValidated.discount_type === 'discount') && 
                     promoValidated.discount_percent && 
                     promoValidated.discount_percent < 100 && (
                      <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                        <p className="text-green-800 font-semibold">
                          Your {promoValidated.discount_percent}% discount will be applied at checkout!
                        </p>
                        <p className="text-green-600 text-sm mt-1">
                          Original: ${tour?.base_price ? tour.base_price.toFixed(2) : '0.00'} → 
                          <span className="font-bold ml-1">
                            ${tour?.base_price ? ((tour.base_price) * (1 - (promoValidated.discount_percent || 0) / 100)).toFixed(2) : '0.00'}
                          </span>
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Only show payment section if promo code is NOT a free tour (100% off) */}
              {(!showPromoRedeemForm || (promoValidated && promoValidated.discount_type !== 'free' && (promoValidated.discount_percent || 0) !== 100)) && (
                <>
                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-300"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-4 bg-gray-50 text-gray-500 font-semibold">OR PAY WITH CARD</span>
                    </div>
                  </div>

                  <button
                    onClick={handlePurchase}
                    disabled={purchasing}
                    data-testid="purchase-tour-button"
                    className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 text-white py-5 rounded-full font-black text-2xl uppercase shadow-xl hover:shadow-2xl transition-all active:scale-95 disabled:cursor-not-allowed tracking-wide"
                  >
                    {purchasing ? 'Processing...' : 'Buy Tour - 7 Days Access'}
                  </button>

                  <p className="text-base text-gray-600 text-center mt-6 font-semibold">
                    Access expires 7 days after purchase. Total devices: {1 + additionalDevices}
                  </p>
                </>
              )}
              </>
              )}
            </div>
            
            {/* Reviews Section */}
            <Reviews tourId={tourId} showWriteReview={true} />
            
            {/* Contact Section */}
            <div className="mt-10 pt-8 border-t-2 border-gray-200">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-gradient-to-r from-gray-50 to-cyan-50 p-6 rounded-xl">
                <div>
                  <h4 className="text-lg font-bold text-gray-900">Have questions or feedback?</h4>
                  <p className="text-gray-600">We'd love to hear from you. Send us a private message.</p>
                </div>
                <ContactButton tourId={tourId} tourTitle={tour.title} variant="default" />
              </div>
            </div>
          </div>
        </div>
      </main>

      {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} />}
    </div>
  );
};

export default TourDetailPage;